import React, { useRef } from 'react';
import './ParticipantGroupColorPicker.css';
import { colorVar, COLOR_KEY_LABELS, TEMPLATE_COLOR_KEYS } from '../../../util/participantGroups';

/**
 * Swatch picker over the fixed palette (decision 10). `keys` = the offered colors: for groups the palette
 * minus the current role colors (groupColorKeys — a role overrides the group color of the person dot),
 * for roles every color except "neutral".
 * Radio group semantics: one tab stop, arrow keys move the selection.
 */
function ParticipantGroupColorPicker({
  value, onChange, keys = TEMPLATE_COLOR_KEYS, disabled = false, ariaLabel = 'Farbe der Gruppe',
}) {
  const refs = useRef({});
  const selected = keys.includes(value) ? value : null;
  // Tab stop: the selected swatch, otherwise the first one.
  const focusKey = selected ?? keys[0];

  const select = (key) => {
    if (disabled || key === selected) return;
    onChange(key);
  };

  const handleKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = keys[(index + step + keys.length) % keys.length];
    select(next);
    refs.current[next]?.focus();
  };

  return (
    <div className="groupColorPicker">
      <div className="groupColorPickerSwatches" role="radiogroup" aria-label={ariaLabel}>
        {keys.map((key, index) => {
          const isSelected = key === selected;
          return (
            <button
              key={key}
              ref={el => { refs.current[key] = el; }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={COLOR_KEY_LABELS[key]}
              title={COLOR_KEY_LABELS[key]}
              tabIndex={key === focusKey ? 0 : -1}
              disabled={disabled}
              className={`groupColorSwatch${key === 'neutral' ? ' isNeutral' : ''}${isSelected ? ' isSelected' : ''}`}
              style={{ '--group-swatch-color': colorVar(key) }}
              onClick={() => select(key)}
              onKeyDown={e => handleKeyDown(e, index)}
            >
              {isSelected && <span className="material-symbols-rounded groupColorSwatchCheck" aria-hidden="true">check</span>}
            </button>
          );
        })}
      </div>
      <span className="groupColorPickerLabel">{COLOR_KEY_LABELS[value] ?? ''}</span>
    </div>
  );
}

export default ParticipantGroupColorPicker;
